/**
 * Subscriptions API service
 */
import { z } from 'zod';
import { apiClient, createFormData } from './client';
import type { PricingPlan, SubscriptionStatus, CheckoutResponse, MessageResponse, Language } from '@/types';

interface PricingPlansResponse {
  plans: Record<string, PricingPlan>;
}

/**
 * The checkout's 409: the customer already has a subscription in Stripe
 * (one per customer), so plan changes go through the customer portal.
 * Matches the rejection shape of the client's response interceptor.
 */
const subscriptionExistsErrorSchema = z.object({
  status: z.literal(409),
  data: z.object({
    detail: z.object({ code: z.literal('subscription_exists') }),
  }),
});

export function isSubscriptionExistsError(error: unknown): boolean {
  return subscriptionExistsErrorSchema.safeParse(error).success;
}

export const subscriptionsApi = {
  /**
   * Get all pricing plans
   */
  getPricingPlans: async (): Promise<PricingPlansResponse> => {
    const response = await apiClient.get<PricingPlansResponse>('/pricing/plans');
    return response.data;
  },

  /**
   * Get subscription plans (alias)
   */
  getSubscriptionPlans: async (): Promise<PricingPlansResponse> => {
    const response = await apiClient.get<PricingPlansResponse>('/subscription/plans');
    return response.data;
  },

  /**
   * Get current user's subscription status
   */
  getSubscriptionStatus: async (): Promise<SubscriptionStatus> => {
    const response = await apiClient.get<SubscriptionStatus>('/subscription/status');
    return response.data;
  },

  /**
   * Get subscription features for current user
   */
  getFeatures: async (): Promise<{ features: string[]; can_access_premium: boolean }> => {
    const response = await apiClient.get<{ features: string[]; can_access_premium: boolean }>(
      '/subscription/features'
    );
    return response.data;
  },

  /**
   * Create a checkout session for a plan. `language` sets the Stripe Checkout locale
   * where Stripe has the language (en hr sl bg ro el tr); the backend uses "auto" otherwise.
   */
  createCheckout: async (planId: string, language: Language): Promise<CheckoutResponse> => {
    const formData = createFormData({ plan_id: planId, lang: language });
    const response = await apiClient.post<CheckoutResponse>('/subscription/checkout', formData, {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    });
    return response.data;
  },

  /**
   * Cancel current subscription
   */
  cancelSubscription: async (): Promise<MessageResponse> => {
    const response = await apiClient.post<MessageResponse>('/subscription/cancel');
    return response.data;
  },

  /**
   * Create customer portal session
   */
  createPortalSession: async (): Promise<{ url: string }> => {
    const response = await apiClient.get<{ portal_url: string }>('/subscription/portal');
    return { url: response.data.portal_url };
  },

  /**
   * Verify checkout session after successful payment
   */
  verifySession: async (
    sessionId: string
  ): Promise<{
    success: boolean;
    subscription_status: string;
    is_premium: boolean;
    subscription_expires: string | null;
  }> => {
    const response = await apiClient.post<{
      success: boolean;
      subscription_status: string;
      is_premium: boolean;
      subscription_expires: string | null;
    }>('/payment/verify-session', { session_id: sessionId });
    return response.data;
  },
};

export default subscriptionsApi;
